import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Store } from 'lucide-react';
import { slugify } from '@garlic/shared';
import { api, ApiError } from '../lib/api';
import { Alert, Button, Card, Field, GarlicLogo, Input } from '../ui/primitives';

type Recent = { slug: string; name: string };
const RECENT_KEY = 'garlic:recent-restaurants';

/** Lembra (só neste navegador) os painéis abertos, para a equipe voltar com um clique. */
export function rememberRestaurant(slug: string, name: string) {
  try {
    const list = readRecent().filter((r) => r.slug !== slug);
    localStorage.setItem(RECENT_KEY, JSON.stringify([{ slug, name }, ...list].slice(0, 5)));
  } catch {
    /* armazenamento indisponível (aba anônima, bloqueado): só não lembra */
  }
}

function readRecent(): Recent[] {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((r): r is Recent => typeof r?.slug === 'string' && typeof r?.name === 'string') : [];
  } catch {
    return [];
  }
}

/** Aceita o slug, o nome do restaurante ou um link colado (…/r/bella-italia, …/painel/bella-italia). */
function toSlug(input: string) {
  const fromUrl = /\/(?:r|painel)\/([a-z0-9-]+)/i.exec(input);
  return (fromUrl ? fromUrl[1]! : slugify(input)).toLowerCase();
}

/**
 * garlicfood.com.br/painel — entrada da área administrativa dos restaurantes.
 * Só localiza o restaurante; o login (e toda autorização) acontece no painel dele, /painel/{slug}.
 * Não existe busca por e-mail aqui, para não revelar a quais restaurantes um e-mail pertence.
 */
export function PanelEntry() {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const recent = readRecent();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const slug = toSlug(value);
    if (!slug) return setError('Digite o endereço do restaurante.');
    setBusy(true);
    setError(undefined);
    try {
      const ctx = await api<{ restaurant: { slug: string } }>(`/api/t/${encodeURIComponent(slug)}/context`);
      navigate(`/painel/${ctx.restaurant.slug}`);
    } catch (err) {
      setError(err instanceof ApiError && err.status === 404 ? `Não encontramos o restaurante "${slug}". Confira o endereço com o administrador.` : 'Falha de conexão. Tente novamente.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center bg-paper-2 px-4 py-10">
      <div className="w-full max-w-sm">
        <Link to="/" className="mb-6 inline-flex items-center gap-1 text-sm text-ink-3 hover:text-ink">
          <ArrowLeft className="size-3.5" /> garlicfood.com.br
        </Link>
        <Card className="p-6">
          <GarlicLogo />
          <h1 className="mt-5 text-lg font-semibold leading-tight">Área do restaurante</h1>
          <p className="mt-1 text-sm text-ink-3">Informe o endereço do seu restaurante no Garlic para entrar no painel.</p>
          <form onSubmit={submit} className="mt-5 space-y-4">
            {error && <Alert tone="danger">{error}</Alert>}
            <Field label="Endereço do restaurante" hint="Ex.: bella-italia (de garlicfood.com.br/r/bella-italia)">
              {(id) => <Input id={id} autoComplete="off" autoCapitalize="none" spellCheck={false} placeholder="bella-italia" required value={value} onChange={(e) => setValue(e.target.value)} />}
            </Field>
            <Button type="submit" variant="primary" className="w-full" loading={busy} icon={<ArrowRight className="size-4" />}>Continuar</Button>
          </form>
          {recent.length > 0 && (
            <div className="mt-6 border-t border-line pt-4">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-3">Usados neste aparelho</p>
              <ul className="space-y-1">
                {recent.map((r) => (
                  <li key={r.slug}>
                    <Link to={`/painel/${r.slug}`} className="flex items-center gap-2 rounded-xl px-2 py-2 text-sm hover:bg-paper-2">
                      <Store className="size-4 text-ink-3" aria-hidden />
                      <span className="flex-1 truncate font-medium">{r.name}</span>
                      <span className="truncate text-xs text-ink-3">/{r.slug}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      </div>
    </main>
  );
}
